import AuthContext from "context/AuthContext";
import dayjs from "dayjs";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import Pagination from "pages/Pagination";
import { useCallback, useContext, useEffect, useState } from "react";
// import { FaExternalLinkAlt } from "react-icons/fa";
import { FaCopy } from "react-icons/fa";
import { TiPencil, TiPlus, TiTrash } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import { apiDelete, apiGet, apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";
import { formatTime12h } from "utils/timeFormat";

const pageSize = 10;

function TrainingSessions() {
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const { user } = useContext(AuthContext);
  const notification = useToastContext();
  const navigate = useNavigate();
  const permissions = user?.permission?.find(
    (item) => item.manager === "training_session_manager",
  );
  const canEdit = permissions?.add || user?.role === "admin";
  const canDelete = permissions?.delete || user?.role === "admin";

  async function copySessionLink(sessionId) {
    const url = `${process.env.REACT_APP_WEBSITE_BASE_URL}/training-sessions/${sessionId}`;
    try {
      await navigator.clipboard.writeText(url);
      notification.success("Training session link copied.");
    } catch (error) {
      notification.error("Unable to copy training session link.");
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setKeyword(search.trim());
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await apiGet(apiPath.trainingSessions, {
        page,
        pageSize,
        ...(keyword && { keyword }),
      });
      setSessions(response?.data?.results?.docs || []);
      setTotalPages(response?.data?.results?.totalPages || 0);
      setTotalItems(response?.data?.results?.totalDocs || 0);
    } catch (error) {
      notification.error(
        error?.response?.data?.message || "Unable to load training sessions.",
      );
    }
  }, [keyword, notification, page]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  async function deleteSession(session) {
    try {
      const response = await apiDelete(
        `${apiPath.trainingSessions}/${session._id}`,
      );
      if (!response?.data?.success) return;

      notification.success("Training session deleted successfully.");
      if (sessions.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        fetchSessions();
      }
    } catch (error) {
      notification.error(
        error?.response?.data?.message || "Unable to delete training session.",
      );
    }
  }

  async function changeStatus(session) {
    try {
      const status = session.status === "active" ? "inactive" : "active";
      const response = await apiPut(
        `${apiPath.trainingSessions}/change-status/${session._id}`,
        { status },
      );
      if (!response?.data?.success) return;

      notification.success(
        `Training session ${status === "active" ? "enabled" : "disabled"} successfully.`,
      );
      fetchSessions();
    } catch (error) {
      notification.error(
        error?.response?.data?.message ||
          "Unable to change training session status.",
      );
    }
  }

  return (
    <div className="flex flex-col gap-4 bg-white px-2 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Training Sessions</h1>
        {canEdit && (
          <button
            type="button"
            onClick={() => navigate("/training-sessions/add")}
            className="flex items-center gap-2 rounded-lg bg-[#8FC150] px-5 py-2.5 text-sm text-white hover:bg-[#7eae43]"
          >
            <TiPlus /> Create Training Session
          </button>
        )}
      </div>

      <div className="relative overflow-x-auto border">
        <div className="flex justify-end px-2 py-3">
          <label className="sr-only" htmlFor="training-session-search">
            Search training sessions by location
          </label>
          <input
            id="training-session-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by location"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 sm:max-w-[320px]"
          />
        </div>

        <table className="w-full text-left text-xs text-[#A5A5A5]">
          <thead className="border border-[#E1E6EE] bg-[#E1E6EE] text-xs text-gray-900">
            <tr>
              <th className="px-6 py-3">#</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Start Time</th>
              <th className="px-6 py-3">End Time</th>
              <th className="px-6 py-3">Location</th>
              <th className="px-6 py-3">Seats</th>
              <th className="px-6 py-3 text-center">Status</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session, index) => (
              <tr className="border-b border-[#E1E6EE]" key={session._id}>
                <td className="border-l px-6 py-3">
                  {(page - 1) * pageSize + index + 1}
                </td>
                <td className="border-l px-6 py-3">
                  {dayjs(session.date).isValid()
                    ? dayjs(session.date).format("DD-MM-YYYY")
                    : "N/A"}
                </td>
                <td className="border-l px-6 py-3">
                  {formatTime12h(session.startTime)}
                </td>
                <td className="border-l px-6 py-3">
                  {formatTime12h(session.endTime)}
                </td>
                <td className="border-l px-6 py-3">
                  {session.location || "N/A"}
                </td>
                <td className="border-l px-6 py-3">
                  {session.seats ?? "N/A"}
                </td>
                <td className="border-l px-6 py-3 text-center">
                  {canEdit ? (
                    <label
                      className="relative inline-flex cursor-pointer items-center"
                      title={
                        session.status === "active" ? "Active" : "Inactive"
                      }
                    >
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={session.status === "active"}
                        onChange={(e) =>
                          helpers.alertFunction(
                            `Are you sure you want to ${
                              e.target.checked ? "enable" : "disable"
                            } this training session?`,
                            session,
                            changeStatus,
                          )
                        }
                      />
                      <div className="peer h-5 w-10 rounded-full bg-gray-200 peer-checked:bg-[#8FC150] peer-focus:ring-0 peer-focus:ring-blue-300 after:absolute after:left-[2px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800" />
                    </label>
                  ) : (
                    <span
                      className={
                        session.status === "active"
                          ? "text-green-500"
                          : "text-red-500"
                      }
                    >
                      {session.status === "active" ? "Active" : "Inactive"}
                    </span>
                  )}
                </td>
                <td className="border-l px-6 py-3">
                  <div className="flex justify-center gap-2">
                    {/* <button
                      type="button"
                      title="View submissions"
                      className="rounded-full bg-[#8FC150] p-2 text-white"
                      onClick={() =>
                        navigate(
                          `/training-sessions/submissions?trainingSessionId=${session._id}`,
                        )
                      }
                    >
                      <FaExternalLinkAlt />
                    </button> */}
                    <button
                      type="button"
                      title="Copy training session link"
                      className="rounded-full bg-[#8FC150] p-2 text-white hover:bg-[#7eae43] transition-colors duration-150"
                      onClick={() => copySessionLink(session._id)}
                    >
                      <FaCopy />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        title="Edit training session"
                        className="rounded-full bg-blue-500 p-2 text-white hover:bg-blue-700 transition-colors duration-150"
                        onClick={() =>
                          navigate(`/training-sessions/edit/${session._id}`)
                        }
                      >
                        <TiPencil />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        title="Delete training session"
                        className="rounded-full bg-red-500 p-2 text-white hover:bg-red-700 transition-colors duration-150"
                        onClick={() =>
                          helpers.alertFunction(
                            `Are you sure want to delete ${
                              session.location || "this training session"
                            }?`,
                            session,
                            deleteSession,
                            true,
                          )
                        }
                      >
                        <TiTrash />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {isEmpty(sessions) && (
              <tr className="border-b bg-white text-center">
                <td className="px-6 py-4" colSpan={8}>
                  No training sessions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalItems > 0 && (
          <Pagination
            handlePageClick={({ selected }) => setPage(selected + 1)}
            options={{
              totalItems,
              pageCount: totalPages,
              pageRangeDisplayed: 5,
            }}
            page={page}
          />
        )}
      </div>

    </div>
  );
}

export default TrainingSessions;
